import { aesGcmOpen } from '@/crypto/aes-gcm'
import { base64ToBytes, bytesToBase64 } from '@/crypto/encoding'
import { prepareRecoveryReset } from '@/crypto/key-hierarchy'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import * as defaultApi from '../api/emergencyAccessApi'
import {
  generateEmergencyKeypair,
  unwrapEmergencyPrivKey,
  unwrapSkForGranter,
  wrapEmergencyPrivKey,
  wrapSkForGrantee,
} from './emergencyCrypto'
import type { CreateGrantResponseWire, UnlockResponseWire } from '../types'

/**
 * Track F: Emergency Access flow orchestrators (KEY_HIERARCHY.md §4.7.5)。
 *
 * 各 flow は sessionStore が `'unlocked'` を要求する (SK が memory 上にある状態)。
 * Error 経路は EmergencyError で wrap し、UI 側で messageKey に変換する
 * (CLAUDE.md §2 ルール 10)。
 */

export class EmergencyError extends Error {
  public readonly code:
    | 'no_session'
    | 'already_initialized'
    | 'self_invite'
    | 'duplicate_invite'
    | 'not_found'
    | 'bad_request'
    | 'forbidden'
    | 'too_many_requests'
    | 'network'
    | 'generic'

  public constructor(code: EmergencyError['code']) {
    super(code)
    this.code = code
    this.name = 'EmergencyError'
  }
}

const mapApiError = (err: unknown): EmergencyError => {
  if (err instanceof ApiError) {
    if (err.status === 401) return new EmergencyError('no_session')
    if (err.status === 403) return new EmergencyError('forbidden')
    if (err.status === 404) return new EmergencyError('not_found')
    if (err.status === 409) return new EmergencyError('already_initialized')
    if (err.status === 429) return new EmergencyError('too_many_requests')
    if (err.status === 400) return new EmergencyError('bad_request')

    return new EmergencyError('network')
  }

  return new EmergencyError('generic')
}

const requireUnlockedSk = (): Uint8Array => {
  const state = sessionStore.getState()
  if (state.kind !== 'unlocked') throw new EmergencyError('no_session')

  return state.symmetricKey
}

// ── F-C7 deps shape (spec で API 差し替え可能) ──────────────

export interface EmergencyFlowDeps {
  api: typeof defaultApi
}

const defaultDeps: EmergencyFlowDeps = { api: defaultApi }

// ── initEmergencyKeypairFlow ────────────────────────────────

/**
 * grantee 側: RSA-OAEP-2048 keypair を生成 + SK で privKey を wrap して server に upload。
 *
 * 重い (keygen ~500ms)。UI 側で spinner 表示推奨。
 * 同じ user で 2 回目以降は server が 409 を返すので `already_initialized` を catch する。
 */
export const initEmergencyKeypairFlow = async (
  deps: EmergencyFlowDeps = defaultDeps,
): Promise<void> => {
  const sk = requireUnlockedSk()

  const { pubKeySpki, privKeyPkcs8 } = await generateEmergencyKeypair()
  try {
    const { wrappedPrivKey, iv } = await wrapEmergencyPrivKey(privKeyPkcs8, sk)

    try {
      await deps.api.initEmergencyKeypair({
        emergencyPubKey: bytesToBase64(pubKeySpki),
        wrappedEmergencyPrivKey: wrappedPrivKey,
        wrappedEmergencyPrivKeyIv: iv,
        emergencyKeyVersion: 1,
      })
    } catch (err: unknown) {
      throw mapApiError(err)
    }
  } finally {
    // PKCS#8 平文を zeroize
    privKeyPkcs8.fill(0)
  }
}

// ── createGrantFlow (granter side) ─────────────────────────

/**
 * granter 側: invite + (necessary なら) wrap を 1 step で実行。
 *
 * server から返る `requiresWrap=true` の場合のみ続けて wrapSk + PATCH を呼ぶ。
 * placeholder (`requiresWrap=false`) の場合は wrap せず、invite 行のみが残る
 * (grantee が登録 + init-keypair 後、granter が再 invite or wrap retry する想定)。
 */
export const createGrantFlow = async (
  granteeEmail: string,
  waitDays: number,
  accessType: 'view' | 'takeover',
  deps: EmergencyFlowDeps = defaultDeps,
): Promise<{ id: string; requiresWrap: boolean }> => {
  const sk = requireUnlockedSk()

  let invite: CreateGrantResponseWire
  try {
    invite = await deps.api.createGrant({ granteeEmail, waitDays, accessType })
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      // 409 = 同じ相手への二重招待 (createGrant 固有)。400 = 自己招待。
      if (err.status === 409) throw new EmergencyError('duplicate_invite')
      if (err.status === 400) throw new EmergencyError('self_invite')
    }
    throw mapApiError(err)
  }

  if (!invite.requiresWrap) {
    return { id: invite.id, requiresWrap: false }
  }

  // grantee の pubkey で SK を wrap → server に PATCH
  const wrappedSk = await wrapSkForGrantee(invite.granteePubKey, sk)
  try {
    await deps.api.wrapGrant(invite.id, { wrappedSk })
  } catch (err: unknown) {
    throw mapApiError(err)
  }

  return { id: invite.id, requiresWrap: true }
}

// ── rewrapGrantFlow (granter side, L-24) ───────────────────

/**
 * granter 側: placeholder で `wrappedSk=NULL` のまま残った grant を、grantee が
 * 後から初期化した emergency pubkey で再 wrap する (L-24)。
 *
 * grantee 未初期化のまま invite すると placeholder grant になり、grantee が後で
 * 初期化しても wrappedSk は NULL のまま = unlock が 400 になる。本 flow は
 * `listGrants` が返す `granteePubKey` (grantee の実 pubkey) で SK を wrap し直し、
 * `PATCH /grants/:id/wrap` で埋める。
 */
export const rewrapGrantFlow = async (
  id: string,
  granteePubKey: string,
  deps: EmergencyFlowDeps = defaultDeps,
): Promise<void> => {
  const sk = requireUnlockedSk()

  const wrappedSk = await wrapSkForGrantee(granteePubKey, sk)
  try {
    await deps.api.wrapGrant(id, { wrappedSk })
  } catch (err: unknown) {
    throw mapApiError(err)
  }
}

// ── unlockGranterVaultFlow (grantee side) ──────────────────

export interface DecryptedVaultItem {
  id: string
  type: string
  /** AES-GCM 復号後の JSON 文字列 */
  plaintext: string
  createdAt: string
  updatedAt: string
}

/**
 * grantee 側: granter の vault を unlock。
 *
 *   1. POST /received/:id/unlock → wrappedSk + vaultItems を取得
 *   2. 自分の wrappedEmergencyPrivKey を SK で unwrap → CryptoKey
 *   3. RSA-OAEP で wrappedSk を unwrap → SK_granter (32 byte)
 *   4. 各 vaultItem を SK_granter で AES-GCM decrypt
 *   5. SK_granter を zeroize
 *
 * 戻り値の `plaintext` は cipher の中身 (JSON 文字列)、UI 側で type ごとに parse する。
 *
 * 重要: SK_granter の生存範囲は本関数内のみ。caller の state に流出させない。
 * 復号済 plaintext は UI 側で route 離脱時に zeroize されるべきだが、JS 文字列は
 * GC まかせで完全な保証はない (CLAUDE.md §6.8 documents this limitation)。
 */
export const unlockGranterVaultFlow = async (
  id: string,
  deps: EmergencyFlowDeps = defaultDeps,
): Promise<{ granterEmail: string; granterName: string | null; items: DecryptedVaultItem[] }> => {
  const mySk = requireUnlockedSk()

  let unlockResp: UnlockResponseWire
  try {
    unlockResp = await deps.api.unlockReceived(id)
  } catch (err: unknown) {
    throw mapApiError(err)
  }

  // 1) emergencyPrivKey を SK で unwrap → CryptoKey
  //    server から myWrappedEmergencyPrivKey + IV を 1 req で受け取るので追加 endpoint 不要。
  const granteePrivKey = await unwrapEmergencyPrivKey(
    unlockResp.myWrappedEmergencyPrivKey,
    unlockResp.myWrappedEmergencyPrivKeyIv,
    mySk,
  )

  // 2) RSA-OAEP で wrappedSk を unwrap → SK_granter (32 byte)
  const skGranter = await unwrapSkForGranter(unlockResp.wrappedSk, granteePrivKey)

  try {
    // 3) 各 vault item を SK_granter で AES-GCM decrypt
    const items: DecryptedVaultItem[] = []
    for (const it of unlockResp.vaultItems) {
      const iv = base64ToBytes(it.iv)
      const sealed = new Uint8Array([...base64ToBytes(it.ciphertext), ...base64ToBytes(it.authTag)])
      const plaintextBytes = await aesGcmOpen(skGranter, iv, sealed)

      items.push({
        id: it.id,
        type: it.type,
        plaintext: new TextDecoder().decode(plaintextBytes),
        createdAt: it.createdAt,
        updatedAt: it.updatedAt,
      })
      plaintextBytes.fill(0)
    }

    return {
      granterEmail: unlockResp.granterEmail,
      granterName: unlockResp.granterName,
      items,
    }
  } finally {
    // SK_granter は本関数の責任 — 必ず zeroize
    skGranter.fill(0)
  }
}

// ── emergencyTakeoverFlow (grantee side、accessType='takeover') ──

/**
 * grantee 側: granter のアカウントをテイクオーバーする (マスターパスワード再設定)。
 *
 *   1. POST /received/:id/unlock → wrappedSk + 自分の wrappedEmergencyPrivKey を取得
 *   2. emergencyPrivKey を SK で unwrap → RSA-OAEP で SK_granter (32 byte) を取り出す
 *   3. `prepareRecoveryReset` で新マスターパスワードから SK_granter を再封印した
 *      暗号材料 (SRP / KDF / protectedSymmetricKey) を生成
 *   4. POST /received/:id/takeover で server へ送信 (granter の user 行を更新)
 *   5. SK_granter を zeroize
 *
 * SK_granter は不変なので granter の保管庫アイテムは再暗号化不要。新マスターパスワードは
 * `prepareRecoveryReset` のスコープ内でのみ使用し、サーバには送信しない (ゼロ知識)。
 */
export const emergencyTakeoverFlow = async (
  id: string,
  newPassword: string,
  deps: EmergencyFlowDeps = defaultDeps,
): Promise<{ granterEmail: string }> => {
  const mySk = requireUnlockedSk()

  let unlockResp: UnlockResponseWire
  try {
    unlockResp = await deps.api.unlockReceived(id)
  } catch (err: unknown) {
    throw mapApiError(err)
  }

  const granteePrivKey = await unwrapEmergencyPrivKey(
    unlockResp.myWrappedEmergencyPrivKey,
    unlockResp.myWrappedEmergencyPrivKeyIv,
    mySk,
  )

  const skGranter = await unwrapSkForGranter(unlockResp.wrappedSk, granteePrivKey)

  try {
    const body = await prepareRecoveryReset({
      email: unlockResp.granterEmail,
      newPassword,
      symmetricKey: skGranter,
    })

    try {
      await deps.api.takeoverReceived(id, body)
    } catch (err: unknown) {
      throw mapApiError(err)
    }

    return { granterEmail: unlockResp.granterEmail }
  } finally {
    // SK_granter は本関数の責任 — 必ず zeroize
    skGranter.fill(0)
  }
}
